import { useEffect, useLayoutEffect, useRef } from 'react'
import { useAppLayoutContext } from './useAppLayoutContext'
import type { DependencyList } from 'react'

// Monotonic counters used so a page's unmount cleanup only clears breadcrumb
// state it actually owns. Without this, when the next route mounts before the
// previous route's cleanup fires (TanStack Router concurrent transitions),
// the stale cleanup overwrites the new page's breadcrumb with null.
let breadcrumbVersion = 0
let breadcrumbActionsVersion = 0

export interface PageLayoutConfig {
  /** Secondary sidebar content */
  sidebarContent?: React.ReactNode
  /** Whether to show the header */
  showHeader?: boolean
  /** Breadcrumb navigation content */
  breadcrumb?: React.ReactNode
  /** Right-side action buttons in the breadcrumb bar */
  breadcrumbActions?: React.ReactNode
}

/**
 * usePageLayout - Simplified hook for configuring page layout
 *
 * Provides a cleaner interface for setting secondary sidebar and header visibility.
 * Automatically cleans up on unmount.
 *
 * Pass `deps` when sidebar content depends on async data (e.g. from RxDB). The
 * effect re-runs when any dep changes, so the sidebar is updated once data loads.
 * Keep deps stable (e.g. `id` strings) to avoid infinite loops — do NOT pass the
 * `sidebarContent` node itself as a dep.
 *
 * @param config - Page layout configuration
 * @param deps   - Optional dependency list (defaults to `[]` = run once on mount)
 *
 * @example
 * ```tsx
 * // Static configuration (no deps needed)
 * usePageLayout({ sidebarContent: <TaskSidebar /> })
 *
 * // Dynamic: re-run when project loads from RxDB
 * usePageLayout(
 *   { sidebarContent: project ? <ProjectSidebar projectId={project.id} /> : null },
 *   [project?.id],
 * )
 * ```
 */
export function usePageLayout(config: PageLayoutConfig, deps: DependencyList = []) {
  const { setSecSidebar, setShowHeader, setBreadcrumb, setBreadcrumbActions } = useAppLayoutContext()

  // Keep a ref so the cleanup closure always sees the latest config
  const configRef = useRef(config)
  configRef.current = config

  // Use layout effect to set values synchronously before paint
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    let ownedBreadcrumbVersion = 0
    let ownedBreadcrumbActionsVersion = 0

    if (configRef.current.sidebarContent !== undefined) {
      setSecSidebar(configRef.current.sidebarContent)
    }
    if (configRef.current.showHeader !== undefined) {
      setShowHeader(configRef.current.showHeader)
    }
    if (configRef.current.breadcrumb !== undefined) {
      ownedBreadcrumbVersion = ++breadcrumbVersion
      setBreadcrumb(configRef.current.breadcrumb)
    }
    if (configRef.current.breadcrumbActions !== undefined) {
      ownedBreadcrumbActionsVersion = ++breadcrumbActionsVersion
      setBreadcrumbActions(configRef.current.breadcrumbActions)
    }

    return () => {
      // Don't clean up sidebarContent — the next route will set its own.
      // Cleaning it up causes a race condition where the parent layout's
      // sidebar is nulled out before the parent's effect can re-assert it,
      // resulting in an empty sidebar during route transitions.
      if (configRef.current.showHeader !== undefined) {
        setShowHeader(true)
      }
      // Only clear breadcrumb if no later page has taken ownership.
      // Otherwise the stale cleanup would null out the new page's breadcrumb.
      if (ownedBreadcrumbVersion && ownedBreadcrumbVersion === breadcrumbVersion) {
        setBreadcrumb(null)
      }
      if (ownedBreadcrumbActionsVersion && ownedBreadcrumbActionsVersion === breadcrumbActionsVersion) {
        setBreadcrumbActions(null)
      }
    }
  // deps controls when the effect re-runs; we read config via ref to stay stable
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
}

/**
 * usePageLayoutControls - Hook that returns layout control functions
 *
 * For components that need direct access to layout control functions.
 * Provides automatic cleanup on unmount.
 *
 * @example
 * ```tsx
 * const { setSidebarContent, setShowHeader } = usePageLayoutControls()
 *
 * useEffect(() => {
 *   setSidebarContent(<CustomSidebar />)
 * }, [setSidebarContent])
 * ```
 */
export function usePageLayoutControls() {
  const { setSecSidebar, setShowHeader, setBreadcrumb, setBreadcrumbActions } = useAppLayoutContext()

  useEffect(() => {
    return () => {
      // Don't clean up sidebar — the next route sets its own (same as usePageLayout).
      setShowHeader(true)
      setBreadcrumb(null)
      setBreadcrumbActions(null)
    }
  }, [setShowHeader, setBreadcrumb, setBreadcrumbActions])

  return {
    setSidebarContent: setSecSidebar,
    setShowHeader,
    setBreadcrumb,
    setBreadcrumbActions,
  }
}
